import React, { ReactElement } from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { HStack } from "web/react/emo/h-stack";
import { Link } from "web/react/emo/link";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { LabelledRow } from "web/react/pages/account/order-history/labelled-row";
import { gettext } from "web/script/modules/django-i18n";
import { formatDateMDYWithSeparator } from "web/script/utils/dates";
import { CheckoutOrderHistoryDetailsSerializer } from "web/types/serializers";
import * as styles from "./order-history-details.css";
import { RetailerOrderDetails } from "./retailer-order-details";

export function OrderHistoryDetails({
    reference,
    date_created,
    retailer_orders,
    shipping_address,
    total_amount,
}: CheckoutOrderHistoryDetailsSerializer): ReactElement {
    const formattedShippingAddress = shipping_address
        ? `${shipping_address.first_name} ${shipping_address.last_name}, ${shipping_address.address1}, ${shipping_address.city}, ${shipping_address.zip}`
        : null;

    return (
        <div className={styles.content}>
            <VStack spacing="md">
                <HStack spacing="xs">
                    <a
                        href="/account/orders/"
                        className={styles.backLink}
                        aria-label={gettext("general.back_btn_text")}
                    >
                        <SVGIcon name="arrow-emo" />
                    </a>
                    <Text as="p" textStyle="hero-s">
                        {gettext("order_history.order_details.title")}
                    </Text>
                </HStack>

                <div className={styles.orderInfoContainer}>
                    <HStack spacing="xs">
                        <SVGIcon name="calendar" className={styles.icon} />
                        <LabelledRow
                            label={gettext("order_history.orders.date")}
                            value={formatDateMDYWithSeparator(date_created)}
                        />
                    </HStack>

                    <HStack spacing="xs">
                        <SVGIcon name="shopping-basket" className={styles.icon} />
                        <LabelledRow
                            label={gettext("order_history.orders.lyst_order_no")}
                            value={reference}
                        />
                    </HStack>
                </div>

                {shipping_address && (
                    <VStack spacing="xs">
                        <Text as="p" textStyle="body-bold">
                            {gettext("order_history.order_details.delivery_address")}
                        </Text>
                        <div className={styles.contentHolder}>
                            <VStack spacing="xxs">
                                <Text as="p" textStyle="body-s">
                                    {shipping_address?.email}
                                </Text>
                                <Text as="p" textStyle="body-s">
                                    {formattedShippingAddress}
                                </Text>
                            </VStack>
                        </div>
                    </VStack>
                )}

                <VStack spacing="md">
                    {retailer_orders.map((order) => (
                        <RetailerOrderDetails key={`order-${order.order_no}`} order={order} />
                    ))}
                </VStack>

                <HStack justify="space-between">
                    <Text as="p" textStyle="body-s-bold">
                        {gettext("order_history.orders.total")}
                    </Text>
                    <Text as="p" textStyle="body-s-bold">
                        {total_amount}
                    </Text>
                </HStack>

                <VStack spacing="xs">
                    <Text as="p" textStyle="body-bold">
                        {gettext("order_history.order_details.help.title")}
                    </Text>

                    <Link
                        color="primary"
                        bold={false}
                        title={gettext("order_history.order_details.help.center")}
                        href="http://help.lyst.com/hc/"
                    />
                    <Link
                        color="primary"
                        bold={false}
                        title={gettext("order_history.order_details.help.contact")}
                        href="https://help.lyst.com/hc/requests/new"
                    />
                    <Link
                        color="primary"
                        bold={false}
                        title={gettext("order_history.order_details.help.faq")}
                        href="https://help.lyst.com/hc/categories/115000534149-FAQ"
                    />
                    <Link
                        color="primary"
                        bold={false}
                        title={gettext("order_history.order_details.help.tc")}
                        href="https://www.lyst.com/help/terms-and-conditions/"
                    />
                </VStack>
            </VStack>
        </div>
    );
}

export default OrderHistoryDetails;
